import { buildPdfExportSource } from './pdf-export-client';
import { buildCustomDashboardCsp } from './srcdoc-wrapper';
import { HTML_TO_IMAGE_PATH } from './vendor-manifest';

const dataInjectionClient = (nonce: string): string => `<script>
(() => {
  'use strict';
  const nonce = ${JSON.stringify(nonce)};
  let accepted = false;

  window.addEventListener('message', (event) => {
    if (accepted || event.source !== window.parent) return;
    if (!event.data || event.data.kind !== 'dashboard-data' || event.data.nonce !== nonce) return;
    accepted = true;
    try {
      const json = new TextDecoder('utf-8', { fatal: true }).decode(event.data.payload);
      const parsed = JSON.parse(json);
      window.sazaeDashboardData = Object.freeze({ meta: parsed.meta, data: parsed.data });
      document.dispatchEvent(new CustomEvent('dashboard-data-ready'));
    } catch (error) {
      console.error('ダッシュボードデータを読み込めませんでした', error);
    }
  });

  window.parent.postMessage({ kind: 'dashboard-ready', nonce }, '*');
})();
<\/script>`;

/**
 * 成果物にファイルダウンロード API `window.sazaeDashboard.download({ filename, mimeType, data, xlsx })` を
 * 公開する。sandbox iframe は自らダウンロードを開始できない（`allow-downloads` を付与しない）ため、
 * バイト列を親へ transfer し、親が allowlist・サイズ上限・ユーザー確認を経て保存する
 * （`dashboard-download.ts`）。`data` は string / Blob / ArrayBuffer / ArrayBufferView を受け付ける。
 * `xlsx: true` のとき `data` は CSV とみなされ、親が xlsx に変換して保存する（`csv-to-xlsx.ts`）。
 * PDF は `window.sazaeDashboard.exportPdf({ filename, element, orientation })` で、要素をページ画像に
 * して親へ渡し、親が PDF を組み立てて保存する（`pdf-export-client.ts` / `images-to-pdf.ts`）。
 */
const downloadClient = (nonce: string, origin: string): string => `<script>
(() => {
  'use strict';
  const nonce = ${JSON.stringify(nonce)};
  const toBuffer = async (data) => {
    if (typeof data === 'string') return new TextEncoder().encode(data).buffer;
    if (data instanceof Blob) return await data.arrayBuffer();
    if (data instanceof ArrayBuffer) return data;
    if (ArrayBuffer.isView(data)) {
      return data.buffer.slice(data.byteOffset, data.byteOffset + data.byteLength);
    }
    throw new TypeError('download data must be string | Blob | ArrayBuffer | ArrayBufferView');
  };
  const download = async ({ filename, mimeType, data, xlsx = false } = {}) => {
    if (typeof filename !== 'string') throw new TypeError('filename must be a string');
    if (typeof xlsx !== 'boolean') throw new TypeError('xlsx must be a boolean');
    const payload = await toBuffer(data);
    window.parent.postMessage(
      { kind: 'dashboard-download', nonce, filename, mimeType, xlsx, payload },
      '*',
      [payload],
    );
  };
  const exportPdf = ${buildPdfExportSource(nonce, `${origin}${HTML_TO_IMAGE_PATH}`)};
  window.sazaeDashboard = Object.freeze({ download, exportPdf });
})();
<\/script>`;

/**
 * 成果物には注入データを `window.sazaeDashboardData = Object.freeze({ meta, data })` として
 * 公開する。freezeの対象は外側のオブジェクトだけである。設定済みなら即時利用し、未設定なら
 * documentで引数なしのCustomEvent `dashboard-data-ready` を待つこと。
 * matching messageのUTF-8 decodeまたはJSON parseに失敗した場合はconsole.errorだけを出し、
 * 値の設定もイベント発火もしない。messageは受理済みとなるため、その後の再注入も受理しない。
 */
export const buildInjectionSrcdoc = (
  dashboardHtml: string,
  nonce: string,
  origin = window.location.origin,
): string =>
  `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="${buildCustomDashboardCsp(origin)}">${dataInjectionClient(nonce)}${downloadClient(nonce, origin)}</head><body>${dashboardHtml}</body></html>`;

/**
 * iframeの準備完了後にデータを一度だけ渡す。
 * ArrayBufferをtransferして親から所有権を手放し、巨大データを親が保持し続けないようにする。
 */
export const attachDashboardDataSender = (
  iframe: HTMLIFrameElement,
  nonce: string,
  payload: ArrayBuffer,
): (() => void) => {
  let sent = false;
  const handleMessage = (event: MessageEvent<unknown>) => {
    if (sent) return;
    const targetWindow = iframe.contentWindow;
    if (!targetWindow || event.source !== targetWindow) return;
    const message = event.data as { kind?: unknown; nonce?: unknown } | null;
    if (message?.kind !== 'dashboard-ready' || message.nonce !== nonce) return;
    targetWindow.postMessage({ kind: 'dashboard-data', nonce, payload }, '*', [payload]);
    sent = true;
    window.removeEventListener('message', handleMessage);
  };
  window.addEventListener('message', handleMessage);
  return () => window.removeEventListener('message', handleMessage);
};

/** handshakeの対応関係を推測されにくくするnonceを生成する。 */
export const createNonce = (): string => {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('');
};
